import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaBan,
  FaDownload,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheck,
  FaSearch,
} from "react-icons/fa";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import CurrencyFormModal from "./CurrencyFormModal";
import CurrencyViewModal from "./CurrencyViewModal";
import Sidebar from "./Sidebar";
import { useScreenPermissions } from "./useScreenPermissions";

const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
    />
  </svg>
);
const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c12.5-12.5 12.5-32.8 0-45.3s32.8-12.5-45.3 0l160 160z"
    />
  </svg>
);
const API_URL = "/api";
// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token'); // Adjust based on where you store your token
};
// Helper function to make authenticated API requests
const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
};

const COLORS = {
  CLOUD: '#FBFBFB',      // cloud white background
  BABY: '#D6EAF8',       // baby blue for headers & primary buttons
  DIVIDER: '#E6EEF2',
  BORDER: 'rgba(226,232,240,1)', // tailwind-slate-200 equivalent
  BABY1 : '#AAD4F1'
};

const CurrencyMaster = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("All");
  const [apiError, setApiError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  const navigate = useNavigate();
  
  // States for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  // Get user permissions
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // pageTheme to pass to Sidebar so it matches SalesOrder look
  const pageTheme = {
    sidebarBg: '#ffffff',          // sidebar background should be white
    // use the same color as your buttons for active item background
    activeBg: COLORS.BABY1,         // <-- matches button color (baby blue)
    activeText: '#000000',         // active item text should be black
    profileBg: COLORS.BABY1,        // profile / logout background baby blue
    // other theme fields are optional; Sidebar should use what it supports
  };
  
  useEffect(() => {
    fetchCurrencies();
  }, []);
  
  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  const fetchCurrencies = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await authenticatedFetch(`${API_URL}/currencies`);
      
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Authentication required. Please log in again.");
        }
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const data = await res.json();
      console.log("API Response:", data); // Debug log
      
      // Handle the specific response structure from your API
      let currenciesArray = [];
      if (data && Array.isArray(data.currencies)) {
        // Use the data directly from API without transformation
        currenciesArray = data.currencies.map(currency => ({
          CurrencyCode: currency.CurrencyCode,
          CurrencyName: currency.CurrencyName,
          Symbol: currency.Symbol,
          DefaultRate: currency.DefaultRate || "1.00",
          Status: currency.Status || 1,
          EffectiveDate: currency.EffectiveDate || new Date().toISOString(),
          LastUpdated: currency.LastUpdated || new Date().toISOString()
        }));
      } else {
        console.warn("Unexpected API response structure:", data);
        currenciesArray = [];
      }
      
      setCurrencies(currenciesArray);
    } catch (error) {
      console.error("Error fetching currencies:", error);
      setApiError(error.message);
      setCurrencies([]);
      
      // If authentication error, redirect to login or show login modal
      if (error.message.includes("Authentication required")) {
        // You can redirect to login page or show a login modal
        // For example: window.location.href = '/login';
      }
    }
    setLoading(false);
  };
  
  // New function to fetch a single currency's details
  const fetchCurrencyDetails = async (currencyCode) => {
    try {
      const res = await authenticatedFetch(`${API_URL}/currencies/${currencyCode}`);
      
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const data = await res.json();
      console.log("Currency details response:", data); // Debug log
      
      if (data && data.currency) {
        return data.currency;
      } else {
        throw new Error("Currency not found");
      }
    } catch (error) {
      console.error("Error fetching currency details:", error);
      throw error;
    }
  };
  
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    
    // Ensure currencies is an array before sorting
    if (!Array.isArray(currencies)) return;
    
    const sortedCurrencies = [...currencies].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      // Handle date fields
      if (key === "LastUpdated" || key === "EffectiveDate") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      // Handle numeric fields
      if (key === "DefaultRate") {
        const aNum = parseFloat(a[key]) || 0;
        const bNum = parseFloat(b[key]) || 0;
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }
      if (key === "CurrencyCode") {
        const aNum = parseInt((a[key] || "").split("-")[1] || "0");
        const bNum = parseInt((b[key] || "").split("-")[1] || "0");
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }
      // Handle string fields
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setCurrencies(sortedCurrencies);
  };
  
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 w-3 h-3" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ml-1 w-3 h-3" />
    ) : (
      <FaSortDown className="ml-1 w-3 h-3" />
    );
  };
  
  const handleView = async (currency) => {
    try {
      // Fetch complete currency details for view
      const completeCurrency = await fetchCurrencyDetails(currency.CurrencyCode);
      setSelectedCurrency(completeCurrency);
      setShowView(true);
    } catch (error) {
      console.error("Error fetching currency details for view:", error);
      alert("Error loading currency details. Please try again.");
    }
  };
  

    const handleLogout = () => {
    navigate('/login');
  };

  const handleEdit = async (currency) => {
    try {
      // Fetch complete currency details for editing
      const completeCurrency = await fetchCurrencyDetails(currency.CurrencyCode);
      setSelectedCurrency(completeCurrency);
      setIsEdit(true);
      setShowForm(true);
    } catch (error) {
      console.error("Error fetching currency details for edit:", error);
      alert("Error loading currency details for editing. Please try again.");
    }
  };
  
  const handleDeactivate = async (currency) => {
    if (!window.confirm("Are you sure you want to deactivate this currency?"))
      return;
    try {
      const response = await authenticatedFetch(
        `${API_URL}/currencies/${currency.CurrencyCode}/deactivate`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to deactivate currency");
      }
      fetchCurrencies();
    } catch (error) {
      console.error("Error deactivating currency:", error);
      alert(
        error.message || "An error occurred while deactivating the currency"
      );
    }
  };
  
  const handleReactivate = async (currency) => {
    if (!window.confirm("Are you sure you want to reactivate this currency?"))
      return;
    try {
      const response = await authenticatedFetch(
        `${API_URL}/currencies/${currency.CurrencyCode}/reactivate`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reactivate currency");
      }
      fetchCurrencies();
    } catch (error) {
      console.error("Error reactivating currency:", error);
      alert(
        error.message || "An error occurred while reactivating the currency"
      );
    }
  };
  
  const handleDownloadCSV = () => {
    // Ensure currencies is an array before processing
    if (!Array.isArray(currencies) || currencies.length === 0) {
      alert("No currency data to export");
      return;
    }
    
    const headers = [
      "CurrencyCode",
      "CurrencyName",
      "Symbol",
      "DefaultRate",
      "Status",
      "EffectiveDate",
      "LastUpdated",
    ];
    const rows = currencies.map((c) => [
      c.CurrencyCode || "",
      c.CurrencyName || "",
      c.Symbol || "",
      c.DefaultRate || "",
      getStatusText(c.Status),
      c.EffectiveDate ? dayjs(c.EffectiveDate).format("YYYY-MM-DD") : "",
      c.LastUpdated ? dayjs(c.LastUpdated).format("YYYY-MM-DD HH:mm") : "",
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "currencies.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const handleCreate = () => {
    setSelectedCurrency(null);
    setIsEdit(false);
    setShowForm(true);
  };
  
  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      let response;
      if (isEdit && selectedCurrency) {
        response = await authenticatedFetch(
          `${API_URL}/currencies/${selectedCurrency.CurrencyCode}`,
          {
            method: "PUT",
            body: JSON.stringify(formData),
          }
        );
      } else {
        response = await authenticatedFetch(`${API_URL}/currencies`, {
          method: "POST",
          body: JSON.stringify(formData),
        });
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save currency");
      }
      
      setShowForm(false);
      fetchCurrencies();
    } catch (error) {
      console.error("Error saving currency:", error);
      alert(error.message || "An error occurred while saving the currency");
    } finally {
      setFormLoading(false);
    }
  };
  
  const getStatusColor = (status) => {
    const isActive =
      status === true || status === "Active" || status === 1 || status === "1";
    return isActive ? "bg-teal-50 text-teal-700 hover:bg-teal-100" : "bg-amber-50 text-amber-600 hover:bg-amber-200";
  };
  
  const getStatusText = (status) => {
    if (status === true || status === 1 || status === "1") return "Active";
    if (status === false || status === 0 || status === "0") return "Inactive";
    return status || "Unknown";
  };
  
  // Filter currencies based on search term and filters
  const filteredCurrencies = Array.isArray(currencies) ? currencies.filter((currency) => {
    if (!currency || typeof currency !== 'object') return false;
    
    const matchesSearch =
      searchTerm === "" ||
      (currency.CurrencyCode && currency.CurrencyCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (currency.CurrencyName && currency.CurrencyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (currency.Symbol && currency.Symbol.toLowerCase().includes(searchTerm.toLowerCase()));
      
    const currencyStatus = getStatusText(currency.Status);
    const matchesStatus =
      status === "All" ||
      (status === "Active" && currencyStatus === "Active") ||
      (status === "Inactive" && currencyStatus === "Inactive");
      
    return matchesSearch && matchesStatus;
  }) : [];
  
  // Pagination logic
  const totalPages = Math.ceil(filteredCurrencies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currenciesForPage = filteredCurrencies.slice(startIndex, endIndex);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    
    pages.push(
      <button
        key="prev"
        className="p-2 transition-all duration-200"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={currentPage === 1 ? { color: '#9CA3AF', cursor: 'not-allowed' } : { color: '#2563eb' }}
      >
        <LeftArrowIcon className="w-3 h-3" />
      </button>
    );
    
    for (let i = startPage; i <= endPage; i++) {
      const isActive = currentPage === i;
      pages.push(
        <button
          key={i}
          className={`px-2 py-1 font-medium transition-all duration-200 ${isActive ? '' : 'hover:bg-gray-100'}`}
          onClick={() => handlePageChange(i)}
          style={isActive ? { backgroundColor: COLORS.BABY1, color: '#0F172A' } : { color: '#2563eb' }}
        >
          {i}
        </button>
      );
    }
    
    pages.push(
      <button
        key="next"
        className="p-2 transition-all duration-200"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
        style={currentPage === totalPages || totalPages === 0 ? { color: '#9CA3AF', cursor: 'not-allowed' } : { color: '#2563eb' }}
      >
        <RightArrowIcon className="w-3 h-3" />
      </button>
    );
    
    return pages;
  };
  
  // Calculate stats with safety checks
  const totalCurrencies = Array.isArray(currencies) ? currencies.length : 0;
  const activeCurrencies = Array.isArray(currencies) 
    ? currencies.filter(c => getStatusText(c.Status) === "Active").length 
    : 0;
  const inactiveCurrencies = Array.isArray(currencies) 
    ? currencies.filter(c => getStatusText(c.Status) === "Inactive").length 
    : 0;
  const thisMonthCurrencies = Array.isArray(currencies) 
    ? currencies.filter(c => {
        const updatedDate = new Date(c.LastUpdated);
        const now = new Date();
        return (
          updatedDate.getMonth() === now.getMonth() &&
          updatedDate.getFullYear() === now.getFullYear()
        );
      }).length 
    : 0;
  
  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission('CURRENCY_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('CURRENCY_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('CURRENCY_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('CURRENCY_MASTER', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center" style={{ backgroundColor: COLORS.CLOUD }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto" style={{ borderTopColor: COLORS.BABY, borderBottomColor: COLORS.BABY }}></div>
          <p className="mt-4 text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex" style={{ backgroundColor: COLORS.CLOUD }}>
      {/* pass pageTheme to Sidebar so it can style active item / icons */}
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={pageTheme} 
      onLogout={handleLogout}/>
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* small scoped CSS to force a continuous divider across the entire row (fixes broken right-edge divider) */}
        <style>{`
          /* target only this table */
          table.custom-table { border-collapse: separate; border-spacing: 0; width: 100%; }
          table.custom-table thead th { background-clip: padding-box; }
          /* draw a full-width 1px line for every row using :after so it spans the whole row including the actions column */
          table.custom-table tbody tr { position: relative; }
          table.custom-table tbody tr::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            height: 1px;
            background: #CBD5E1; /* divider color */
            bottom: 0;
            pointer-events: none;
          }
          /* remove per-td bottom borders so we rely on the row line */
          table.custom-table tbody td { border-bottom: none; }
          /* ensure header sticks cleanly */
          table.custom-table thead th.sticky { position: sticky; top: 0; z-index: 20; }
        `}</style>

        {/* Header Section */}
        <div className="mb-8 border-b" style={{ borderColor: COLORS.BORDER, paddingBottom: 16 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                Currency Master
              </h1>
              <p className="mt-1 text-base text-gray-600">
                Manage your currency exchange rates and information
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="flex items-center gap-2 px-5 py-2 bg-white border font-medium transition-all duration-200 shadow-sm"
                onClick={handleDownloadCSV}
                style={{ borderColor: COLORS.BORDER, color: '#0F172A' }}
              >
                <FaDownload className="w-4 h-4" />
                Export
              </button>
              {canCreate && (
                <button
                  onClick={handleCreate}
                  className="flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm"
                  style={{ backgroundColor: COLORS.BABY1, color: '#0F172A' }}
                >
                  <FaPlus className="w-4 h-4" />
                  Create New
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* API Error DISPLAY */}
        {apiError && (
          <div className="mb-6 p-4 bg-red-100 border" style={{ borderColor: COLORS.BORDER }}>
            <p className="text-sm">{`Error loading currencies: ${apiError}`}</p>
            <div className="mt-2 flex gap-2">
              <button 
                onClick={fetchCurrencies}
                className="px-3 py-1 rounded-none hover:bg-red-600 text-white text-sm"
                style={{ backgroundColor: '#EF4444' }}
              >
                Retry
              </button>
              {apiError.includes("Authentication required") && (
                <button 
                  onClick={() => {
                    window.location.href = '/login';
                  }}
                  className="px-3 py-1 rounded-none hover:opacity-90 text-sm"
                  style={{ backgroundColor: COLORS.BABY, color: '#0F172A' }}
                >
                  Login
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">
                  Total Currencies
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {totalCurrencies}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-slate-100 text-slate-900">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">
                  Active Currencies
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {activeCurrencies}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-green-50 text-green-600">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">
                  Inactive Currencies
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {inactiveCurrencies}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-red-50 text-red-600">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">
                  This Month
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {thisMonthCurrencies}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-purple-50 text-purple-600">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {/* Search and Filters (sharp) */}
        <div className="p-5 bg-white border mb-6" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: '#6b7280' }}>What are you looking for?</label>
              <div className="relative mt-2">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for currency code, name, symbol, etc"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                  style={{ borderColor: COLORS.BORDER }}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: '#6b7280' }}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <button 
              className="px-5 py-2 font-medium transition-all duration-200 shadow-sm"
              onClick={() => {
                setCurrentPage(1); // Reset to first page when searching
              }}
              style={{ backgroundColor: COLORS.BABY1, color: '#0F172A' }}
            >
              Search
            </button>
          </div>
        </div>
        
        {/* Currencies Table (sharp) */}
        <section className="bg-white border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: COLORS.BORDER }}>
            <h2 className="text-xl font-semibold text-slate-900">
              Currency Summary
            </h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-gray-500">
                {renderPaginationButtons()}
              </div>
            </div>
          </div>

          <div className="h-full overflow-x-auto">
            <table
              className="w-full custom-table"
              style={{ /* ensure correct rendering */ }}
            >
              <thead className="text-xs font-semibold uppercase text-gray-600" style={{ backgroundColor: COLORS.BABY,  borderBottom: `2px solid ${COLORS.DIVIDER}` }}>
                <tr>
                  <th
                    className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""} sticky`}
                    onClick={() => handleSort("CurrencyCode")}
                    style={{ backgroundColor: COLORS.BABY }}
                  >
                    <div className="flex items-center uppercase">
                      Currency Code {getSortIcon("CurrencyCode")}
                    </div>
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""} sticky`}
                    onClick={() => handleSort("CurrencyName")}
                    style={{ backgroundColor: COLORS.BABY }}
                  >
                    <div className="flex items-center uppercase">
                      Currency Name {getSortIcon("CurrencyName")}
                    </div>
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""} sticky`}
                    onClick={() => handleSort("Symbol")}
                    style={{ backgroundColor: COLORS.BABY }}
                  >
                    <div className="flex items-center uppercase">
                      Symbol {getSortIcon("Symbol")}
                    </div>
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""} sticky`}
                    onClick={() => handleSort("DefaultRate")}
                    style={{ backgroundColor: COLORS.BABY }}
                  >
                    <div className="flex items-center uppercase">
                      Default Rate {getSortIcon("DefaultRate")}
                    </div>
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""} sticky`}
                    onClick={() => handleSort("Status")}
                    style={{ backgroundColor: COLORS.BABY }}
                  >
                    <div className="flex items-center">
                      STATUS {getSortIcon("Status")}
                    </div>
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""} sticky`}
                    onClick={() => handleSort("EffectiveDate")}
                    style={{ backgroundColor: COLORS.BABY }}
                  >
                    <div className="flex items-center uppercase">
                      Effective Date {getSortIcon("EffectiveDate")}
                    </div>
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-3 text-left text-xs font-semibold cursor-pointer transition-all duration-200 ${isScrolled ? "shadow" : ""} sticky`}
                    onClick={() => handleSort("LastUpdated")}
                    style={{ backgroundColor: COLORS.BABY }}
                  >
                    <div className="flex items-center uppercase">
                      Last Updated {getSortIcon("LastUpdated")}
                    </div>
                  </th>
                  <th
                    className={`sticky top-0 z-20 px-6 py-3 text-center text-xs font-semibold transition-all duration-200 sticky`}
                    style={{ backgroundColor: COLORS.BABY }}
                  >
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-8 text-gray-500 font-semibold"
                    >
                      Loading currencies...
                    </td>
                  </tr>
                ) : filteredCurrencies.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-8 text-gray-500 font-semibold"
                    >
                      {apiError ? "Failed to load currencies" : "No currencies found."}
                    </td>
                  </tr>
                ) : (
                  currenciesForPage.map((currency, idx) => (
                    <tr
                      key={currency.CurrencyCode || idx}
                      className={`transition-all duration-200 group hover:bg-slate-50`}
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap uppercase">
                        <p
                          className={`text-slate-900 transition-all duration-200 cursor-pointer ${!canView ? 'pointer-events-none' : ''}`}
                          onClick={() => canView && handleView(currency)}
                        >
                          {currency.CurrencyCode || "N/A"}
                        </p>
                      </td>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap font-semibold uppercase">
                        {currency.CurrencyName || "N/A"}
                      </td>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap font-bold text-lg uppercase">
                        {currency.Symbol || "N/A"}
                      </td>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap text-gray-600">
                        {currency.DefaultRate ? parseFloat(currency.DefaultRate).toFixed(2) : "N/A"}
                      </td>
                      <td className="px-6 py-3 text-left text-sm">
                        <span
                          className={`inline-block px-3 py-1 text-xs font-bold transition-all duration-200 uppercase rounded-full ${getStatusColor(currency.Status)}`}
                        >
                          {getStatusText(currency.Status)}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap text-gray-600 uppercase">
                        {currency.EffectiveDate
                          ? dayjs(currency.EffectiveDate).format("DD MMM YYYY")
                          : "N/A"}
                      </td>
                      <td className="px-6 py-3 text-left text-sm whitespace-nowrap">
                        {currency.LastUpdated ? (
                          <div className="flex flex-col leading-tight uppercase">
                            <span>
                              {dayjs(currency.LastUpdated).format(
                                "DD MMM YYYY"
                              )}
                            </span>
                            <span className="text-xs text-gray-500">
                              {dayjs(currency.LastUpdated).format("HH:mm")}
                            </span>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td className="px-6 py-3 flex gap-2 justify-center items-center">
                        {canView && (
                          <button
                            title="View"
                            className="p-2 rounded-none text-gray-500 hover:bg-slate-100 transition-all duration-150"
                            onClick={() => handleView(currency)}
                          >
                            <FaEye />
                          </button>
                        )}
                        {canEdit && (
                          <button
                            title="Edit"
                            className="p-2 rounded-none text-gray-500 hover:bg-slate-100 transition-all duration-150"
                            onClick={() => handleEdit(currency)}
                          >
                            <FaEdit />
                          </button>
                        )}
                        {canDelete && (
                          <>
                            {getStatusText(currency.Status) === "Active" ? (
                              <button
                                title="Deactivate"
                                className="p-2 rounded-none hover:bg-red-50 text-red-500 transition-all duration-150"
                                onClick={() => handleDeactivate(currency)}
                              >
                                <FaBan />
                              </button>
                            ) : (
                              <button
                                title="Reactivate"
                                className="p-2 rounded-none hover:bg-green-50 text-green-500 transition-all duration-150"
                                onClick={() => handleReactivate(currency)}
                              >
                                <FaCheck />
                              </button>
                            )}
                          </>
                        )}
                        <button
                          title="Download CSV"
                          className="p-2 rounded-none hover:bg-slate-100 text-gray-500 transition-all duration-150"
                          onClick={handleDownloadCSV}
                        >
                          <FaDownload />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
        
        {/* Modals */}
        <CurrencyFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedCurrency}
          isEdit={isEdit}
          loading={formLoading}
        />
        <CurrencyViewModal
          open={showView}
          onClose={() => setShowView(false)}
          currency={selectedCurrency}
        />
      </main>
    </div>
  );
};
export default CurrencyMaster;
